import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Separator } from './ui/separator';
import { Trash2, Plus, FileText, Mail, Printer, Hash, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useToast } from '../hooks/use-toast';
import EngravingSelector from './EngravingSelector';
import QuotePreview from './QuotePreview';
import { mockData } from '../utils/mockData';

// ============================================
// CONFIGURATION - Options masquables
// ============================================
// Mettre à true pour réactiver l'option Câble 220mm
const SHOW_CABLE_OPTION = false;
// ============================================

const QuoteGenerator = () => {
  const [items, setItems] = useState([]);
  const [nextId, setNextId] = useState(1);
  const { toast } = useToast();
  const [glueQuantity, setGlueQuantity] = useState(1);
  const [glueEnabled, setGlueEnabled] = useState(false);
  const [cableEnabled, setCableEnabled] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewContent, setPreviewContent] = useState('');
  
  const [clientInfo, setClientInfo] = useState({
    firstName: '',
    name: '',
    company: '',
    agency: '',
    email: '',
    phone: '',
    chantier: '',
    notes: ''
  });
  const [quoteNumber, setQuoteNumber] = useState('');
  
  // États pour les menus déroulants société/agence
  const [selectedCompany, setSelectedCompany] = useState('');
  const [isCustomCompany, setIsCustomCompany] = useState(false);
  const [isCustomAgency, setIsCustomAgency] = useState(false);
  const [availableAgencies, setAvailableAgencies] = useState([]);
  
  // États pour les adresses
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [billingAddress, setBillingAddress] = useState('');
  
  // Pas besoin d'état pour les onglets car il n'y a plus qu'un seul tableau

  // Fonction pour générer un nouveau numéro de devis (appelée à la finalisation)
  const generateQuoteNumber = () => {
    const now = new Date();
    // Format: AAMMJJ (année sur 2 chiffres)
    const year = now.getFullYear().toString().slice(-2); // 26 au lieu de 2026
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');
    const dateStr = `${year}${month}${day}`;
    // Format: HHMM (sans les secondes)
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    const timeStr = `${hours}${minutes}`;
    const firstNameInitial = (clientInfo.firstName.charAt(0) || 'X').toUpperCase();
    const nameInitial = (clientInfo.name.charAt(0) || 'X').toUpperCase();
    const companyInitial = (clientInfo.company.charAt(0) || 'X').toUpperCase();
    return `DEV-${dateStr}-${timeStr}-${firstNameInitial}${nameInitial}${companyInitial}`;
  };

  // Gérer le changement de société
  const handleCompanyChange = (value) => {
    if (value === 'custom') {
      setIsCustomCompany(true);
      setSelectedCompany('');
      setClientInfo({...clientInfo, company: '', agency: ''});
      setAvailableAgencies([]);
      setBillingAddress(''); // Reset billing address
    } else {
      setIsCustomCompany(false);
      setSelectedCompany(value);
      setClientInfo({...clientInfo, company: value, agency: ''});
      
      // Mettre à jour les agences disponibles
      const agencies = mockData.companiesData[value]?.agencies || [];
      setAvailableAgencies(agencies);
      setIsCustomAgency(false);
      
      // Définir l'adresse de facturation pour AAI
      if (value === 'AAI') {
        setBillingAddress('AAI Castets (Siège) - 92 rue de Lesté, 40260 CASTETS');
      } else {
        setBillingAddress('');
      }
    }
  };

  // Gérer le changement d'agence
  const handleAgencyChange = (value) => {
    if (value === 'custom') {
      setIsCustomAgency(true);
      setClientInfo({...clientInfo, agency: ''});
      setDeliveryAddress(''); // Reset delivery address
    } else {
      setIsCustomAgency(false);
      setClientInfo({...clientInfo, agency: value});
      setDeliveryAddress(value); // L'agence complète devient l'adresse de livraison
    }
  };

  // Calculate price based on exact dimensions and quantity of engraved items
  const calculatePrice = (forme, dimension, gravure) => {
    if (!forme || !dimension) return 0;
    
    // Get base price per piece from mockData
    const basePricePerPiece = mockData.dimensionsWithPrices[dimension] || 0;
    
    if (!gravure || gravure.trim() === '') {
      return basePricePerPiece; // Just one piece if no engraving
    }
    
    // Count number of engraved items (split by comma and filter non-empty)
    const engravedItems = gravure.split(',')
      .map(item => item.trim())
      .filter(item => item.length > 0);
    
    const quantity = engravedItems.length;
    
    // Price = base price per piece × number of pieces
    return basePricePerPiece * quantity;
  };

  // Plus besoin de calcul de prix pour schémas

  const addJetonItem = () => {
    const newItem = {
      id: nextId,
      forme: '',
      couleur: '',
      dimension: '',
      gravure: '',
      price: 0,
      type: 'jeton'
    };
    setItems([...items, newItem]);
    setNextId(nextId + 1);
  };

  const removeItem = (id) => {
    setItems(items.filter(item => item.id !== id));
  };

  const updateItem = (id, field, value) => {
    setItems(prevItems => {
      const updatedItems = prevItems.map(item => {
        if (item.id === id) {
          const updatedItem = { ...item, [field]: value };
          
          // Reset dimension when forme changes to ensure compatibility
          if (field === 'forme') {
            updatedItem.dimension = '';
            updatedItem.gravure = '';
            updatedItem.customGravureText = '';
            updatedItem.price = 0;
          } else if (field === 'dimension' || field === 'gravure' || field === 'customGravureText') {
            // Calculer la gravure totale (sélecteur + texte personnalisé)
            const gravureFromSelector = field === 'gravure' ? value : (updatedItem.gravure || '');
            const customText = field === 'customGravureText' ? value : (updatedItem.customGravureText || '');
            
            // Combiner les deux gravures pour le calcul du prix
            let totalGravure = gravureFromSelector;
            if (customText) {
              totalGravure = totalGravure ? totalGravure + ', ' + customText : customText;
            }
            
            // Calculer le prix avec la gravure totale
            updatedItem.price = calculatePrice(updatedItem.forme, updatedItem.dimension, totalGravure);
            updatedItem.totalGravure = totalGravure;
          }
          return updatedItem;
        }
        return item;
      });
      
      // Vérifier si on doit ajouter une nouvelle ligne
      // Une nouvelle ligne est ajoutée si la dernière ligne a commencé à être remplie
      const lastItem = updatedItems[updatedItems.length - 1];
      if (lastItem && (lastItem.forme || lastItem.couleur || lastItem.dimension)) {
        // Vérifier qu'il n'y a pas déjà une ligne vide à la fin
        const hasEmptyLastRow = !lastItem.forme && !lastItem.couleur && !lastItem.dimension;
        if (!hasEmptyLastRow) {
          // Ajouter une nouvelle ligne vide
          const newItem = {
            id: nextId,
            forme: '',
            couleur: '',
            dimension: '',
            gravure: '',
            customGravureText: '',
            price: 0
          };
          setNextId(prev => prev + 1);
          return [...updatedItems, newItem];
        }
      }
      
      return updatedItems;
    });
  };

  const getDimensionsForShape = (forme) => {
    return mockData.dimensionsByForme[forme] || [];
  };

  // Calculer le nombre de câbles (jetons Rond 40mm et Carré 40x40mm)
  const calculateCableQuantity = () => {
    let cableCount = 0;
    items.forEach(item => {
      // Utiliser totalGravure si disponible, sinon gravure
      const gravure = item.totalGravure || item.gravure;
      if (!gravure || gravure.trim() === '') return;
      
      // Compter les pièces gravées
      const engravedItems = gravure.split(',').map(g => g.trim()).filter(g => g.length > 0);
      const quantity = engravedItems.length;
      
      // Vérifier si c'est un Rond 40mm ou Carré 40x40mm
      if ((item.forme === 'Rond' && item.dimension === '40 mm') ||
          (item.forme === 'Carré' && item.dimension === '40x40 mm')) {
        cableCount += quantity;
      }
    });
    return cableCount;
  };

  const cableQuantity = calculateCableQuantity();
  // Le prix câble est 0 si l'option SHOW_CABLE_OPTION est désactivée
  const cablePrice = SHOW_CABLE_OPTION && cableEnabled ? cableQuantity * 0.18 : 0;

  const totalHT = items.reduce((sum, item) => sum + (item.price || 0), 0);
  const gluePrice = glueEnabled ? glueQuantity * 11 : 0;
  const finalTotalHT = totalHT + cablePrice + gluePrice;

  const handlePreview = () => {
    // Validation stricte des champs obligatoires
    if (!clientInfo.firstName || !clientInfo.name || !clientInfo.company) {
      toast({
        title: "Informations manquantes",
        description: "Veuillez remplir le Prénom, le Nom et la Société avant de continuer",
        variant: "destructive"
      });
      return;
    }

    const filteredItems = items.filter(item => item.forme || item.couleur || item.dimension || item.gravure || item.customGravureText).map(item => ({
      ...item,
      // Utiliser totalGravure pour l'affichage sur le devis
      gravure: item.totalGravure || item.gravure || ''
    }));
    
    if (filteredItems.length === 0) {
      toast({
        title: "Aucun article",
        description: "Veuillez ajouter au moins un article pour générer l'aperçu",
        variant: "destructive"
      });
      return;
    }

    // Générer un nouveau numéro de devis unique à chaque finalisation
    const newQuoteNumber = generateQuoteNumber();
    setQuoteNumber(newQuoteNumber);

    const previewData = QuotePreview({
      quoteNumber: newQuoteNumber,
      clientInfo,
      items: filteredItems,
      totalHT: finalTotalHT,
      glueQuantity: glueEnabled ? glueQuantity : 0,
      cableQuantity: SHOW_CABLE_OPTION && cableEnabled ? cableQuantity : 0,
      deliveryAddress,
      billingAddress
    });
    
    // Try popup first, fallback to modal
    try {
      const previewWindow = window.open('', '_blank', 'width=1024,height=768,scrollbars=yes,resizable=yes');
      if (previewWindow && !previewWindow.closed) {
        previewWindow.document.write(previewData.htmlContent);
        previewWindow.document.close();
        previewWindow.focus();
        
        toast({
          title: "Aperçu généré",
          description: "Le devis s'ouvre dans une nouvelle fenêtre avec bouton d'impression"
        });
      } else {
        throw new Error('Popup blocked');
      }
    } catch (error) {
      // Fallback: show in modal
      setPreviewContent(previewData.htmlContent);
      setShowPreview(true);
      
      toast({
        title: "Aperçu généré",
        description: "Le devis s'affiche ci-dessous (popup bloqué par le navigateur)"
      });
    }
  };

  const handlePrint = () => {
    handlePreview(); // Same as preview for now
  };

  const handleSendEmail = () => {
    // Validation stricte des champs obligatoires
    if (!clientInfo.firstName || !clientInfo.name || !clientInfo.company) {
      toast({
        title: "Informations manquantes",
        description: "Veuillez remplir le Prénom, le Nom et la Société avant de continuer",
        variant: "destructive"
      });
      return;
    }

    if (!clientInfo.email) {
      toast({
        title: "Email manquant",
        description: "Veuillez remplir l'email du client",
        variant: "destructive"
      });
      return;
    }

    const filteredItems = items.filter(item => item.forme || item.couleur || item.dimension || item.gravure || item.customGravureText).map(item => ({
      ...item,
      // Utiliser totalGravure pour l'affichage sur le devis
      gravure: item.totalGravure || item.gravure || ''
    }));
    if (filteredItems.length === 0) {
      toast({
        title: "Aucun article",
        description: "Veuillez ajouter au moins un article au devis",
        variant: "destructive"
      });
      return;
    }

    // Générer un nouveau numéro de devis unique si pas encore généré
    const emailQuoteNumber = quoteNumber || generateQuoteNumber();
    if (!quoteNumber) {
      setQuoteNumber(emailQuoteNumber);
    }

    const previewData = QuotePreview({
      quoteNumber: emailQuoteNumber,
      clientInfo,
      items: filteredItems,
      totalHT: finalTotalHT,
      glueQuantity: glueEnabled ? glueQuantity : 0,
      cableQuantity: SHOW_CABLE_OPTION && cableEnabled ? cableQuantity : 0,
      deliveryAddress,
      billingAddress
    });

    // Create email content
    const subject = `Devis ${emailQuoteNumber} - ${clientInfo.name} (${clientInfo.company})`;
    const emailBody = `Bonjour,

Voici votre devis ToK'anD :

${previewData.textContent}

Cordialement,
L'équipe ToK'anD`;

    // Send to both client and company
    const clientMailto = `mailto:${clientInfo.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(emailBody)}`;
    const companyMailto = `mailto:tok.and@outlook.fr?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(emailBody)}`;

    // Open both email clients
    window.open(clientMailto, '_blank');
    setTimeout(() => {
      window.open(companyMailto, '_blank');
    }, 1000);

    toast({
      title: "Emails préparés",
      description: `Devis ${quoteNumber} envoyé à ${clientInfo.email} et tok.and@outlook.fr`
    });
  };

  const clearAll = () => {
    setItems([]);
    setNextId(1);
  };

  // Initialize with some empty rows
  useEffect(() => {
    if (items.length === 0) {
      // Ajouter 3 items de jeton par défaut
      for (let i = 0; i < 3; i++) {
        addJetonItem();
      }
    }
  }, []);

  // S'assurer qu'il y a toujours au moins une ligne vierge pour les jetons
  useEffect(() => {
    const jetonItems = items.filter(item => item.type === 'jeton' || !item.type);
    
    if (jetonItems.length === 0) {
      addJetonItem();
    }
  }, [items]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-700 via-teal-700 to-cyan-600 p-4 main-container">
      <div className="max-w-7xl mx-auto card-container">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-white text-4xl font-bold tracking-tight mb-2">
            Devis instantané ToK'anD
          </h1>
          <p className="text-emerald-100">
            Choisissez vos options, voyez le devis en direct, puis envoyez votre commande.
          </p>
        </div>

        <Card className="bg-white/95 backdrop-blur-sm shadow-2xl">
          {/* Action Bar */}
          <CardHeader className="border-b border-slate-200">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">
                  Devis client
                </div>
                <span className="text-sm text-slate-600">
                  {quoteNumber ? `N° ${quoteNumber}` : 'N° (généré à la finalisation)'}
                </span>
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-6">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Client Information */}
              <div className="lg:col-span-1">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Informations client</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4 mobile-padding">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 form-grid client-form-grid">
                      <div>
                        <Label htmlFor="clientFirstName">Prénom *</Label>
                        <Input
                          id="clientFirstName"
                          value={clientInfo.firstName}
                          onChange={(e) => setClientInfo({...clientInfo, firstName: e.target.value})}
                          placeholder="Prénom du client"
                          className="border-2"
                        />
                      </div>
                      <div>
                        <Label htmlFor="clientName">Nom *</Label>
                        <Input
                          id="clientName"
                          value={clientInfo.name}
                          onChange={(e) => setClientInfo({...clientInfo, name: e.target.value})}
                          placeholder="Nom du client"
                          className="border-2"
                        />
                      </div>
                      <div>
                        <Label htmlFor="clientCompany">Société *</Label>
                        {isCustomCompany ? (
                          <div className="space-y-2">
                            <Input
                              id="clientCompany"
                              value={clientInfo.company}
                              onChange={(e) => setClientInfo({...clientInfo, company: e.target.value})}
                              placeholder="Nom de la société"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => handleCompanyChange('')}
                              className="w-full text-xs"
                            >
                              Retour aux sociétés existantes
                            </Button>
                          </div>
                        ) : (
                          <select
                            className="flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-white px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                            value={selectedCompany}
                            onChange={(e) => handleCompanyChange(e.target.value)}
                          >
                            <option value="">Choisir une société...</option>
                            {Object.keys(mockData.companiesData).map((company) => (
                              <option key={company} value={company}>
                                {company}
                              </option>
                            ))}
                            <option value="custom">+ Nouvelle société</option>
                          </select>
                        )}
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="clientAgency">Agence</Label>
                      {isCustomAgency || availableAgencies.length === 0 ? (
                        <div className="space-y-2">
                          <Input
                            id="clientAgency"
                            value={clientInfo.agency}
                            onChange={(e) => setClientInfo({...clientInfo, agency: e.target.value})}
                            placeholder="Nom ou référence de l'agence"
                          />
                          {availableAgencies.length > 0 && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setIsCustomAgency(false);
                                setClientInfo({...clientInfo, agency: ''});
                              }}
                              className="w-full text-xs"
                            >
                              Retour aux agences existantes
                            </Button>
                          )}
                        </div>
                      ) : (
                        <select
                          className="flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                          value={clientInfo.agency}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === 'custom') {
                              setIsCustomAgency(true);
                              setClientInfo({...clientInfo, agency: ''});
                              setDeliveryAddress('');
                            } else {
                              setIsCustomAgency(false);
                              setClientInfo({...clientInfo, agency: value});
                              setDeliveryAddress(value);
                            }
                          }}
                        >
                          <option value="">Choisir une agence...</option>
                          {availableAgencies.map((agency) => (
                            <option key={agency} value={agency}>
                              {agency}
                            </option>
                          ))}
                          <option value="custom">+ Nouvelle agence</option>
                        </select>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 form-grid">
                      <div>
                        <Label htmlFor="clientEmail">Email *</Label>
                        <Input
                          id="clientEmail"
                          type="email"
                          value={clientInfo.email}
                          onChange={(e) => setClientInfo({...clientInfo, email: e.target.value})}
                          placeholder="email@exemple.com"
                        />
                      </div>
                      <div>
                        <Label htmlFor="clientChantier">Chantier</Label>
                        <Input
                          id="clientChantier"
                          value={clientInfo.chantier}
                          onChange={(e) => setClientInfo({...clientInfo, chantier: e.target.value})}
                          placeholder="Nom ou référence du chantier"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="clientPhone">Téléphone</Label>
                      <Input
                        id="clientPhone"
                        type="tel"
                        value={clientInfo.phone}
                        onChange={(e) => setClientInfo({...clientInfo, phone: e.target.value})}
                        placeholder="Numéro de téléphone"
                      />
                    </div>
                    <div>
                      <Label htmlFor="clientNotes">Commentaires</Label>
                      <Textarea
                        id="clientNotes"
                        value={clientInfo.notes}
                        onChange={(e) => setClientInfo({...clientInfo, notes: e.target.value})}
                        placeholder="Détails complémentaires..."
                        rows={3}
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Items Table */}
              <div className="lg:col-span-2">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Articles</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-md font-medium">Jetons et plaques</h3>
                      <div className="flex gap-2">
                        <Button onClick={addJetonItem} size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                          <Plus className="h-4 w-4 mr-1" />
                          Ajouter
                        </Button>
                        <Button onClick={clearAll} variant="outline" size="sm">
                          <Trash2 className="h-4 w-4 mr-1" />
                          Vider
                        </Button>
                      </div>
                    </div>
                    <div className="overflow-x-auto table-container" translate="no">
                      <table className="w-full table-auto">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left px-1 py-2 font-medium text-slate-700 text-sm w-[10%] min-w-[70px]">Forme</th>
                            <th className="text-left px-1 py-2 font-medium text-slate-700 text-sm w-[10%] min-w-[70px]">Couleur</th>
                            <th className="text-left px-1 py-2 font-medium text-slate-700 text-sm w-[12%] min-w-[80px]">Dimension</th>
                            <th className="text-left px-1 py-2 font-medium text-slate-700 text-sm w-[25%] min-w-[150px]">Gravure</th>
                            <th className="text-left px-1 py-2 font-medium text-slate-700 text-sm w-[8%] min-w-[60px]">Sélection rapide</th>
                            <th className="text-right px-1 py-2 font-medium text-slate-700 text-sm w-[18%] min-w-[120px]">Prix HT (€) × Qté</th>
                            <th className="text-center px-1 py-2 font-medium text-slate-700 text-sm w-[7%] min-w-[50px]">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.filter(item => item.type === 'jeton' || !item.type).map((item) => (
                            <tr key={item.id} className="border-b border-slate-100">
                              <td className="px-1 py-1.5">
                                <Select
                                  value={item.forme}
                                  onValueChange={(value) => updateItem(item.id, 'forme', value)}
                                >
                                  <SelectTrigger className="w-full h-8 text-xs mobile-select">
                                    <SelectValue placeholder="Forme" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {mockData.formes.map((forme) => (
                                      <SelectItem key={forme} value={forme}>
                                        <span translate="no">{forme}</span>
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </td>
                              <td className="px-1 py-1.5">
                                <Select
                                  value={item.couleur}
                                  onValueChange={(value) => updateItem(item.id, 'couleur', value)}
                                >
                                  <SelectTrigger className="w-full h-8 text-xs mobile-select">
                                    <SelectValue placeholder="Couleur" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {mockData.couleurs.map((couleur) => (
                                      <SelectItem key={couleur} value={couleur}>
                                        <span translate="no">{couleur}</span>
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </td>
                              <td className="px-1 py-1.5">
                                <Select
                                  value={item.dimension}
                                  onValueChange={(value) => updateItem(item.id, 'dimension', value)}
                                >
                                  <SelectTrigger className="w-full h-8 text-xs mobile-select">
                                    <SelectValue placeholder="Dimension" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {getDimensionsForShape(item.forme).map((dimension) => (
                                      <SelectItem key={dimension} value={dimension}>
                                        <span translate="no">{dimension}</span>
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </td>
                              <td className="px-1 py-1.5">
                                <Input
                                  value={item.totalGravure || item.gravure || item.customGravureText || ''}
                                  onChange={(e) => {
                                    updateItem(item.id, 'customGravureText', e.target.value);
                                    updateItem(item.id, 'gravure', '');
                                    updateItem(item.id, 'totalGravure', e.target.value);
                                  }}
                                  placeholder="Texte à graver"
                                  className="w-full h-8 text-xs mobile-select"
                                />
                              </td>
                              <td className="px-1 py-1.5">
                                <EngravingSelector
                                  value={item.gravure}
                                  onChange={(value) => {
                                    updateItem(item.id, 'gravure', value);
                                    updateItem(item.id, 'totalGravure', value);
                                  }}
                                  itemId={item.id}
                                  selectedColor={item.couleur}
                                  selectedForme={item.forme}
                                />
                              </td>
                              <td className="px-1 py-1.5 text-right">
                                <Input
                                  value={item.price.toFixed(2)}
                                  readOnly
                                  className="w-full h-8 text-right bg-slate-50 text-xs"
                                />
                              </td>
                              <td className="px-1 py-1.5 text-center">
                                <Button
                                  onClick={() => removeItem(item.id)}
                                  variant="outline"
                                  size="sm"
                                  className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    <Separator className="my-4" />
                    
                    {/* Options accessoires */}
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-4">
                      {/* Option Câble 220mm - Masquable via SHOW_CABLE_OPTION */}
                      {SHOW_CABLE_OPTION && (
                      <div>
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            id="cableEnabled"
                            checked={cableEnabled}
                            onChange={(e) => setCableEnabled(e.target.checked)}
                            className="mt-1 h-4 w-4 text-amber-600 border-amber-300 rounded focus:ring-amber-500"
                          />
                          <div className="flex-1">
                            <Label htmlFor="cableEnabled" className="text-sm font-medium text-amber-800 cursor-pointer">
                              Câble 220mm pour jeton - 0,18 € l'unité
                            </Label>
                            <p className="text-xs text-amber-700 mt-1">
                              Pour jetons Rond 40mm et Carré 40x40mm (quantité automatique)
                            </p>
                            {cableEnabled && (
                              <div className="mt-2 bg-amber-100 rounded px-3 py-2">
                                {cableQuantity > 0 ? (
                                  <p className="text-xs text-amber-800 font-medium">
                                    Total câbles: {cableQuantity} × 0,18 € = {cablePrice.toFixed(2)} €
                                  </p>
                                ) : (
                                  <p className="text-xs text-amber-700 italic">
                                    Aucun jeton Rond 40mm ou Carré 40x40mm dans le devis
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      )}
                      
                      <Separator className="border-amber-200" />
                      
                      {/* Option Tube de colle */}
                      <div>
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            id="glueEnabled"
                            checked={glueEnabled}
                            onChange={(e) => setGlueEnabled(e.target.checked)}
                            className="mt-1 h-4 w-4 text-amber-600 border-amber-300 rounded focus:ring-amber-500"
                          />
                          <div className="flex-1">
                            <Label htmlFor="glueEnabled" className="text-sm font-medium text-amber-800 cursor-pointer">
                              Tube de colle tout matériaux 142g - 11 € l'unité
                            </Label>
                            <p className="text-xs text-amber-700 mt-1">
                              Recommandé pour fixer vos schémas
                            </p>
                            {glueEnabled && (
                              <div className="mt-3 flex items-center gap-3">
                                <Label htmlFor="glueQuantity" className="text-xs text-amber-700 whitespace-nowrap">
                                  Quantité:
                                </Label>
                                <div className="flex items-center gap-2 flex-1 max-w-[250px]">
                                  <span className="text-xs text-amber-600 font-medium">1</span>
                                  <input
                                    id="glueQuantity"
                                    type="range"
                                    min="1"
                                    max="10"
                                    value={glueQuantity}
                                    onChange={(e) => setGlueQuantity(parseInt(e.target.value))}
                                    className="flex-1 h-2 bg-amber-200 rounded-lg appearance-none cursor-pointer slider"
                                    style={{
                                      background: `linear-gradient(to right, #f59e0b 0%, #f59e0b ${(glueQuantity - 1) * 11.11}%, #fde68a ${(glueQuantity - 1) * 11.11}%, #fde68a 100%)`
                                    }}
                                  />
                                  <span className="text-xs text-amber-600 font-medium">10</span>
                                  <div className="bg-amber-100 text-amber-800 px-2 py-1 rounded text-xs font-semibold min-w-[24px] text-center">
                                    {glueQuantity}
                                  </div>
                                </div>
                              </div>
                            )}
                            {glueEnabled && (
                              <div className="mt-2 bg-amber-100 rounded px-3 py-2">
                                <p className="text-xs text-amber-800 font-medium">
                                  Total tubes: {glueQuantity} × 11 € = {(glueQuantity * 11).toFixed(2)} €
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Total */}
                    <div className="flex justify-end">
                      <div className="bg-slate-50 rounded-lg p-4 w-64">
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span>Sous-total articles:</span>
                            <span>{totalHT.toFixed(2)} €</span>
                          </div>
                          {SHOW_CABLE_OPTION && cableEnabled && cableQuantity > 0 && (
                            <div className="flex justify-between text-sm text-amber-700">
                              <span>Câble 220mm ({cableQuantity}x):</span>
                              <span>{cablePrice.toFixed(2)} €</span>
                            </div>
                          )}
                          {glueEnabled && (
                            <div className="flex justify-between text-sm text-amber-700">
                              <span>Tube colle ({glueQuantity}x):</span>
                              <span>{(glueQuantity * 11).toFixed(2)} €</span>
                            </div>
                          )}
                          <Separator />
                          <div className="flex justify-between items-center text-lg font-semibold">
                            <span>Total HT:</span>
                            <span className="text-emerald-700">{finalTotalHT.toFixed(2)} €</span>
                          </div>
                        </div>
                        <p className="text-sm text-slate-600 mt-2">
                          Prix calculé par pièce + options (transport inclus dans le devis final)
                        </p>
                        <Button
                          onClick={handlePreview}
                          className="w-full mt-4 bg-emerald-600 hover:bg-emerald-700"
                        >
                          <FileText className="h-4 w-4 mr-2" />
                          Finaliser le devis
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Section détail produit discrète en bas */}
            <div className="mt-8 pt-6 border-t border-slate-200 detail-produit-section">
              <details className="group">
                <summary className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer hover:text-slate-800 transition-colors">
                  <FileText className="h-4 w-4" />
                  <span>Détail produit (cliquer pour plus d'informations)</span>
                  <span className="ml-auto group-open:rotate-180 transition-transform">▼</span>
                </summary>
                
                <div className="mt-4 space-y-6">
                  {/* Caractéristiques produits */}
                  <div className="grid md:grid-cols-2 gap-4 text-xs bg-slate-50 rounded-lg p-4">
                    <div className="space-y-2">
                      <h4 className="font-medium text-slate-700">Carré / Rond (Jetons)</h4>
                      <ul className="space-y-1 text-slate-600">
                        <li>• Gravé au laser</li>
                        <li>• Percé en 4 mm en point haut</li>
                        <li>• Fourni avec chaînette ou câble 220mm</li>
                      </ul>
                    </div>
                    <div className="space-y-2">
                      <h4 className="font-medium text-slate-700">Rectangle (Plaques)</h4>
                      <ul className="space-y-1 text-slate-600">
                        <li>• Gravé au laser</li>
                        <li>• Fourni avec bande autocollante</li>
                      </ul>
                    </div>
                  </div>
                  
                  {/* Préconisations */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
                    <h4 className="font-medium text-amber-800 text-sm">Préconisations</h4>
                    <div className="text-xs text-amber-700 space-y-2">
                      <p>
                        <strong>Fixation plaques :</strong> Pour murs en agglo, béton ou brique, 
                        nous conseillons la colle tout matériaux 142g pour une fixation optimale.
                      </p>
                      {SHOW_CABLE_OPTION && (
                      <p>
                        <strong>Câble 220mm :</strong> Les câbles 220mm offrent une fixation sécurisée qui ne se défait pas 
                        et ne tombe pas, même en cas de vibrations. Si vous ne sélectionnez pas l'option câble, 
                        c'est automatiquement la chaînette qui sera livrée avec vos jetons.
                      </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Galerie photos produits */}
                  <div className="bg-white border border-slate-200 rounded-lg p-4">
                    <h4 className="font-medium text-slate-700 text-sm mb-4">Nos produits</h4>
                    
                    {/* Vue d'ensemble - Image Excel */}
                    <div className="mb-6">
                      <h5 className="text-xs font-medium text-emerald-700 mb-3 uppercase tracking-wide">Vue d'ensemble des formats</h5>
                      <div className="flex justify-center">
                        <img 
                          src="https://customer-assets.emergentagent.com/job_token-quote/artifacts/onin7egf_plaque.png" 
                          alt="Vue d'ensemble des formats de plaques et jetons ToK'anD"
                          className="max-w-full h-auto rounded-lg border border-slate-200"
                        />
                      </div>
                    </div>
                    
                    {/* Accessoires */}
                    <div>
                      <h5 className="text-xs font-medium text-emerald-700 mb-3 uppercase tracking-wide">Accessoires</h5>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {/* Chaînette */}
                        <div className="border border-slate-200 rounded-lg overflow-hidden bg-slate-50">
                          <div className="aspect-square bg-white flex items-center justify-center">
                            <img 
                              src="https://customer-assets.emergentagent.com/job_token-quote/artifacts/v8rifzh7_PHOTO%20CHAINE.jpg" 
                              alt="Chaînette pour jeton"
                              className="w-full h-full object-contain p-2"
                            />
                          </div>
                          <div className="p-2 text-center bg-white border-t border-slate-200">
                            <p className="text-xs font-medium text-slate-700">Chaînette</p>
                            <p className="text-xs text-slate-500">Incluse avec jetons</p>
                          </div>
                        </div>
                        
                        {/* Câble 220mm - Masquable via SHOW_CABLE_OPTION */}
                        {SHOW_CABLE_OPTION && (
                        <div className="border border-slate-200 rounded-lg overflow-hidden bg-slate-50">
                          <div className="aspect-square bg-white flex items-center justify-center">
                            <img 
                              src="https://customer-assets.emergentagent.com/job_token-quote/artifacts/lhdbkj97_PHOTO%20CABLE" 
                              alt="Câble 220mm pour jeton"
                              className="w-full h-full object-contain p-2"
                            />
                          </div>
                          <div className="p-2 text-center bg-white border-t border-slate-200">
                            <p className="text-xs font-medium text-slate-700">Câble 220mm</p>
                            <p className="text-xs text-slate-500">0,18 € / unité</p>
                          </div>
                        </div>
                        )}
                        
                        {/* Tube de colle */}
                        <div className="border border-slate-200 rounded-lg overflow-hidden bg-slate-50">
                          <div className="aspect-square bg-white flex items-center justify-center">
                            <img 
                              src="https://customer-assets.emergentagent.com/job_engraving-system/artifacts/pfqo1jci_2753265_1.jpg" 
                              alt="Tube de colle Pattex Ni Clou Ni Vis 142g"
                              className="w-full h-full object-contain p-2"
                            />
                          </div>
                          <div className="p-2 text-center bg-white border-t border-slate-200">
                            <p className="text-xs font-medium text-slate-700">Tube de colle</p>
                            <p className="text-xs text-slate-500">142g - 11 €</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </details>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Modal d'aperçu de devis */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Aperçu du devis</h3>
              <div className="flex gap-2">
                <Button
                  onClick={() => window.print()}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <Printer className="h-4 w-4 mr-2" />
                  Imprimer
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowPreview(false)}
                >
                  <X className="h-4 w-4 mr-2" />
                  Fermer
                </Button>
              </div>
            </div>
            <div 
              className="overflow-auto max-h-[80vh] p-4"
              dangerouslySetInnerHTML={{ __html: previewContent }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default QuoteGenerator;